<!--
  StoreBranding -- store-editor Branding section.

  Section IA (2026-05-29 campaign-editor design handoff, locked):

    SETUP / "Branding" (ADR 0058 -- storefront look only; email
    customization split off to Email Notifications)
    1. Hero carousel  (NEW)            -- up to 5 images at the top of
                                          the storefront. Slides live on
                                          the carousel_slides endpoint.
                                          Auto-advance interval slider +
                                          per-slide headline/URL editing
                                          live inside this card.
    2. Brand identity                  -- the two halves of visual
                                          identity in a two-column card:
                                          brand IMAGE (left) + brand COLOR
                                          (right). The brand image
                                          (logo/lockup) is uploaded via the
                                          shared ImageUploader and persists
                                          to the store's `brand_image`
                                          column (re-added to the UI per
                                          ADR 0058, reversing PR #2128's
                                          URL-paste removal -- the column
                                          was retained, so no schema
                                          change). It feeds the storefront
                                          header and is the default brand
                                          mark in emails. The brand color
                                          is the primary storefront accent;
                                          toolbar text auto-flips to white
                                          for contrast.
    3. Welcome message  (MOVED FROM    -- short greeting shown to
       PAYMENT & CREDITS)                 shoppers on the storefront
                                          landing page. Persists to the
                                          store's `message` column.

  Save plumbing: this is a store-editor SECTION component. The parent
  (StoreAddEdit.vue) drives autosave + save-campaign by calling
  `tabRef(activeTab).updateCampaign(this.campaign)` and
  `tabRef(11).loadComponent()` on the active tab's ref. Every sibling
  section (StoreSetup, StoreCheckout, StoreTaxLegal, StorefrontPreview,
  ...) exposes both methods; StoreBranding MUST too, or the autosave
  path throws "updateCampaign is not a function" /
  "loadComponent is not a function" (the carousel-upload crash this file
  fixes). updateCampaign folds the branding-owned fields (brand_color,
  brand_image, message) into the campaign and returns it; loadComponent
  is a no-op because branding loads its carousel slides in mounted().

  Brand color / brand image / message are real, persisted columns
  (api/src/models/store.py); editing them in place + emitting
  @campaign-changed is the established section pattern. Carousel slides
  persist directly against the carousel_slides endpoint.

  The data-testid prefix is ct-branding-* for parity with the rest of
  the store-editor.
-->
<template>
  <section
    id="branding"
    class="branding"
    data-testid="ct-branding-root"
  >
    <!-- Section intro: SETUP eyebrow + preamble -->
    <header
      class="branding-intro"
      data-testid="ct-branding-intro"
    >
      <div class="branding-intro__eyebrow">Setup</div>
      <p
        class="branding-intro__lede"
        data-testid="ct-branding-intro-lede"
      >
        How the storefront looks to shoppers -- the carousel at the top,
        your logo and colors, and the welcome message.
      </p>
    </header>

    <!-- 1. Hero carousel card.
         NO eyebrow: this card carried eyebrow="New" from the PR that
         introduced it. "New" is a build-time note, not product copy --
         everything in this product is new, so it told a distributor
         nothing (Jake, 2026-08-12). Same class of leftover as the
         "Moved from Payment & Credits" eyebrow removed earlier; the
         spec below guards both. -->
    <editor-card
      title="Hero carousel"
      description="Up to 5 images shown at the top of your storefront."
    >
      <div
        class="branding-carousel"
        data-testid="ct-branding-carousel-card"
      >
        <!--
          Recommended hero image size. The numbers are DERIVED from how the
          storefront actually renders the band, not invented. They changed
          with the split-band hero (2026-08-14, item 4 of
          docs/planning/2026-08-14-storefront-layout-gap-vs-company-store-comp.md)
          -- the previous "1920 x 220, ~8.7:1" advice described the OLD
          full-width letterbox band and is actively wrong for the new one:

            - StorefrontHero.vue no longer spans the full width with its
              photo. The band is full-bleed but its inner grid is capped at
              --tf-storefront-max-width (1320px) and split into a copy
              column (~55%) and a MEDIA column (~45%). After the band's
              32px side padding and 32px gap, the media column is roughly
              550px on a wide desktop viewport.
            - HERO_MAX_HEIGHT = 380px caps the band height, and any slide
              taller than the band letterboxes with side padding
              (object-fit: contain). ~550x380 is therefore the largest
              image that fills the media column with no bars, i.e. a
              roughly 1.45:1 landscape.
            - The recommendation is stated at 2x (1100 x 760) so the slide
              stays crisp on a retina display. The component never upscales
              past an image's natural pixels (slideImageStyle caps at
              min(100%, naturalWidth)), so oversizing is safe and
              undersizing is what renders small.

          Stated as a ratio too, since the band scales with the viewport --
          the RATIO is what keeps a slide bar-free at any width.
        -->
        <p
          class="branding-card__hint branding-carousel__size-guidance"
          data-testid="ct-branding-hero-size-guidance"
        >
          Recommended size: <strong>1100 x 760 px</strong> (a landscape
          image, about 3:2). The hero shows your store name beside the
          image, so the image fills the right-hand side rather than the full
          page width. Taller or narrower images still work -- they are
          centered with space on the sides, never cropped -- and images
          smaller than this are not enlarged.
        </p>

        <div class="branding-carousel__strip">
          <div
            v-for="slide in orderedSlides"
            :key="slide.id"
            class="branding-carousel__slide"
          >
            <div
              class="branding-carousel__thumb"
              :data-testid="`ct-branding-carousel-thumb-${slide.id}`"
              :data-slide-id="String(slide.id)"
              :draggable="editMode ? 'true' : 'false'"
              :class="{
                'branding-carousel__thumb--active':
                  selectedSlideId === String(slide.id),
                'branding-carousel__thumb--dragging':
                  draggedSlideId === String(slide.id),
              }"
              @click="onThumbClick(slide)"
              @dragstart="onDragStart(slide, $event)"
              @dragover.prevent="onDragOver(slide, $event)"
              @drop.prevent="onDrop(slide)"
              @dragend="onDragEnd"
            >
              <img
                :src="slideImageUrl(slide)"
                :alt="slide.headline || 'Carousel slide'"
                class="branding-carousel__thumb-img"
                @load="onThumbImageLoad(slide.id, $event)"
              />
            </div>
            <!-- Pixel dimensions, read client-side from the loaded image's
                 naturalWidth/naturalHeight (no backend/schema change; works
                 for already-uploaded images). Helps the distributor supply
                 near-matching aspect ratios for the no-crop storefront band. -->
            <div
              v-if="thumbDimsLabel(slide.id)"
              class="branding-carousel__thumb-dims"
              data-testid="hero-slide-dimensions"
            >
              {{ thumbDimsLabel(slide.id) }}
            </div>
          </div>

          <button
            type="button"
            class="branding-carousel__add"
            :class="{ 'branding-carousel__add--disabled': addDisabled }"
            :data-disabled="addDisabled ? 'true' : 'false'"
            :disabled="addDisabled || !editMode"
            data-testid="ct-branding-carousel-add"
            @click="onAddClick"
          >
            <span aria-hidden="true">+</span>
            <span class="branding-carousel__add-label">Add image</span>
          </button>

          <input
            ref="carouselFileInput"
            type="file"
            accept="image/*"
            class="branding-carousel__file-input"
            data-testid="ct-branding-carousel-file-input"
            @change="onFileSelected"
          />
        </div>

        <hero-carousel-editor
          v-if="selectedSlide"
          :slide="selectedSlide"
          @save="onSlideSave"
          @delete="onSlideDelete"
        />

        <!--
          Auto-advance interval. The slider works in SECONDS (2-15) for a
          human-friendly scale; the persisted column is MILLISECONDS
          (stores.hero_carousel_interval_ms), so we convert at the
          binding boundary (carouselIntervalSeconds computed). Auto-saves
          on change via @campaign-changed, matching brand_color / message.
          Vuetify 3: bind @update:modelValue, NOT @change (which does not
          fire on v-slider in Vuetify 3 -- known project gotcha).
        -->
        <div
          class="branding-carousel__interval"
          data-testid="ct-branding-carousel-interval-card"
        >
          <label class="branding-field__label">Seconds between slides</label>
          <p class="branding-card__hint">
            How long each slide shows before the carousel advances.
          </p>
          <div class="branding-carousel__interval-row">
            <span
              class="branding-carousel__interval-bound"
              data-testid="ct-branding-carousel-interval-min"
              >{{ CAROUSEL_INTERVAL_MIN }}s</span
            >
            <v-slider
              :model-value="carouselIntervalSeconds"
              :min="CAROUSEL_INTERVAL_MIN"
              :max="CAROUSEL_INTERVAL_MAX"
              :step="1"
              :disabled="!editMode"
              hide-details
              class="branding-carousel__interval-slider"
              data-testid="ct-branding-carousel-interval"
              @update:modelValue="onCarouselIntervalInput"
            />
            <span
              class="branding-carousel__interval-bound"
              data-testid="ct-branding-carousel-interval-max"
              >{{ CAROUSEL_INTERVAL_MAX }}s</span
            >
          </div>
          <p
            class="branding-carousel__interval-value"
            data-testid="ct-branding-carousel-interval-value"
          >
            {{ carouselIntervalSeconds }}
            {{ carouselIntervalSeconds === 1 ? "second" : "seconds" }}
          </p>
        </div>
      </div>
    </editor-card>

    <!--
      2. Brand identity card (ADR 0058)

      The two halves of visual identity -- the brand IMAGE (logo/lockup)
      and the brand COLOR -- share one card in two side-by-side columns
      (image left, color right). Replaces the former standalone "Brand
      color" card. Stacks to one column on narrow widths (ADR 0024).

      Brand image: re-added to the UI (ADR 0058 reverses PR #2128's
      URL-paste removal). The brand_image store column was retained, so no
      schema change -- we re-surface it via the SHARED ImageUploader
      component, and persist through the SAME campaign autosave allowlist
      path PR-4 used for email_image: POST /upload_image -> set
      campaign.brand_image = uuid -> @campaign-changed. The brand image
      feeds the storefront header and is the default brand mark in emails
      (Store.effective_email_header_image falls back to it).
    -->
    <editor-card
      title="Brand identity"
      description="Your brand's look on the storefront -- your logo and your primary color."
    >
      <div class="branding-identity">
        <!-- LEFT: brand image (logo / lockup) -->
        <div class="branding-identity__col">
          <label class="branding-field__label">Brand image</label>
          <p class="branding-card__hint">
            Your logo or brand lockup, shown in the storefront header and
            used as the default brand mark in emails.
          </p>
          <image-uploader
            testid-prefix="ct-branding-brand-image"
            :current-image="brandImageUrl"
            :disabled="!editMode"
            :uploading="brandImageUploading"
            title="Add a brand image"
            hint="PNG or SVG, transparent background preferred."
            @upload="onBrandImageUpload"
            @remove="onBrandImageRemove"
            @error="onBrandImageError"
          />
        </div>

        <!--
          RIGHT: brand color, specified as a PMS (Pantone) SPOT COLOR.

          Phil asked for PMS rather than RGB because the CODE is what a
          production shop matches thread/ink to; a screen hex cannot
          represent a spot color faithfully. So there are two values:

            campaign.brand_pms_code -- authoritative for production.
            campaign.brand_color    -- the rendered hex. UNCHANGED in
                                       meaning: still what every
                                       storefront surface paints with.

          The hex has TWO write paths and they must persist identically:
            1. pick a shade  -> hex auto-filled from our approximation;
            2. off-list code -> hex supplied by the distributor, because
                                we have no approximation to offer.

          The palette is 576 codes derived from the Madeira thread
          charts vendored for the Wilcom integration, NOT the full
          ~2100-shade PANTONE Coated set. A brand color outside it is
          ordinary, not an error -- hence path 2.

          The native color input below is retained as the direct hex
          control for distributors who only care about the on-screen
          color and have no PMS code.
        -->
        <div class="branding-identity__col">
          <label class="branding-field__label">Brand color</label>
          <p class="branding-card__hint">
            Primary accent color used on the storefront header, buttons,
            and links.
          </p>

          <!-- Currently-selected PMS code, if any -->
          <p
            v-if="brandPmsCode"
            class="branding-pms__selected"
            data-testid="ct-branding-pms-selected"
          >
            <span
              class="branding-pms__selected-swatch"
              :style="{ backgroundColor: brandColor || '#FFFFFF' }"
              data-testid="ct-branding-pms-selected-swatch"
            ></span>
            <strong>{{ brandPmsCode }}</strong>
            <button
              type="button"
              class="branding-pms__clear"
              :disabled="!editMode"
              data-testid="ct-branding-pms-clear"
              @click="onClearPmsCode"
            >
              Clear
            </button>
          </p>

          <!-- Searchable PMS shade picker -->
          <input
            type="text"
            class="branding-pms__search"
            :value="pmsQuery"
            :disabled="!editMode"
            placeholder="Search PMS shades, e.g. 1935 or Black 6"
            data-testid="ct-branding-pms-search"
            @input="onPmsQueryInput"
          />

          <ul
            v-if="pmsQuery && visibleShades.length"
            class="branding-pms__list"
            data-testid="ct-branding-pms-list"
          >
            <li
              v-for="shade in visibleShades"
              :key="shade.code"
              class="branding-pms__option"
              data-testid="ct-branding-pms-option"
              @click="onSelectShade(shade)"
            >
              <span
                class="branding-pms__option-swatch"
                :style="{ backgroundColor: shade.hex }"
                data-testid="ct-branding-pms-option-swatch"
              ></span>
              <span class="branding-pms__option-code">{{ shade.code }}</span>
            </li>
          </ul>

          <p
            v-if="pmsQuery && !visibleShades.length"
            class="branding-card__hint"
            data-testid="ct-branding-pms-no-results"
          >
            No PMS shades match "{{ pmsQuery }}". If your brand color is
            not listed, enter its code below.
          </p>

          <!--
            Free-text escape. Our palette is thread-chart derived and
            partial, so a distributor whose brand color is not in it must
            still be able to record the real code.
          -->
          <button
            type="button"
            class="branding-pms__freetext-toggle"
            :disabled="!editMode"
            data-testid="ct-branding-pms-freetext-toggle"
            @click="onToggleFreeText"
          >
            Not listed? Enter a PMS code
          </button>

          <div
            v-if="freeTextOpen"
            class="branding-pms__freetext"
            data-testid="ct-branding-pms-freetext"
          >
            <input
              type="text"
              class="branding-pms__freetext-input"
              :value="freeTextCode"
              :disabled="!editMode"
              placeholder="e.g. PMS 286 C"
              data-testid="ct-branding-pms-freetext-input"
              @input="onFreeTextCodeInput"
              @change="onFreeTextCodeCommit"
            />

            <!--
              Hex for an off-list code. We have no approximation for a
              code outside our charts, and guessing one would be worse
              than asking: the distributor knows their own brand color.
              Pre-filled from the store's current color so saving can
              never leave the storefront without an accent.
            -->
            <template v-if="freeTextNeedsHex">
              <label
                class="branding-field__label branding-pms__freetext-hex-label"
                data-testid="ct-branding-pms-freetext-hex-label"
              >
                On-screen color for this code
              </label>
              <p
                class="branding-card__hint"
                data-testid="ct-branding-pms-freetext-hex-hint"
              >
                We do not have a screen color on file for this code. Enter
                the hex your brand uses on screen -- production still uses
                the PMS code above.
              </p>
              <input
                type="text"
                class="branding-pms__freetext-hex"
                :value="freeTextHex"
                :disabled="!editMode"
                placeholder="#RRGGBB"
                data-testid="ct-branding-pms-freetext-hex"
                @input="onFreeTextHexInput"
                @change="onFreeTextHexCommit"
              />
            </template>

            <p
              v-if="freeTextError"
              class="branding-pms__error"
              data-testid="ct-branding-pms-freetext-error"
            >
              {{ freeTextError }}
            </p>
          </div>

          <!--
            Approximation disclosure. Load-bearing, not decoration: a
            distributor who trusts the on-screen swatch as their exact
            brand color will be disappointed by the physical product.
          -->
          <p
            class="branding-card__note branding-pms__approximation"
            data-testid="ct-branding-pms-approximation-note"
          >
            Swatches are an <strong>approximation</strong> of a spot color
            -- screens cannot reproduce PMS exactly. The
            <strong>PMS code</strong> is what production matches; the
            on-screen color is only for your storefront.
          </p>

          <div class="branding-card__row">
            <input
              type="color"
              class="branding-color-swatch"
              :value="brandColor"
              :disabled="!editMode"
              data-testid="ct-branding-color-input"
              @input="onBrandColorInput"
            />
            <input
              type="text"
              class="branding-color-hex"
              :value="brandColor"
              :disabled="!editMode"
              placeholder="#000000"
              data-testid="ct-branding-color-hex"
              @input="onBrandColorInput"
            />
          </div>
          <p
            class="branding-card__note branding-contrast-note"
            data-testid="ct-branding-contrast-note"
          >
            Toolbar text auto-flips to white for contrast.
          </p>
        </div>
      </div>
    </editor-card>

    <!--
      2b. White-label branding card (Unit 5, card mq26K91V)

      Replaces the ThreadFlow mark on this store's storefront (and in its
      emails) with the distributor's own. TWO GRAINS: the ENTITLEMENT is
      bought once per DISTRIBUTOR through their subscription tier; the
      ASSETS are set per STORE, because one distributor runs many
      storefronts and brands them differently.

      THE GATE RENDERS NOTHING, NOT A DISABLED CONTROL. ``v-if`` on the
      whole card, never ``:disabled``. PR #2922 catalogued controls that
      exist and do nothing -- a greyed box invites a distributor to click
      it, conclude they configured something, and get no feedback that
      their plan does not include the feature. Absence is honest; a dead
      control is a support ticket.

      THE FLAG IS AUTHORITATIVE, NOT THE ASSETS. The gate reads
      ``whiteLabelEnabled`` (the resolver's tier answer, computed
      server-side) and never "does this store have a logo". A store that
      uploaded one and then lost the entitlement must render ThreadFlow
      again -- reading the asset as the permission is how a lapsed
      subscription keeps paid branding silently and indefinitely.

      THE TOGGLE IS THE SECOND GATE (Unit 8, 2026-09-20). The tier
      entitlement makes this card APPEAR; the toggle inside it is what
      actually switches the store's branding over. Both must be on. Jake:
      "We also need a toggle to turn white-label branding on, so that we
      don't use the ThreadFlow icon when no white-label brand logo is
      uploaded."

      That toggle replaced his earlier rule 3 ("enabled and no logo ->
      show the ThreadFlow logo"), which this card's help text used to
      state. The rule is GONE -- "If the toggle is on, no ThreadFlow logo
      at all" -- so the old hint is now FALSE and has been rewritten. The
      ordering-safety rule 3 bought is bought by the toggle instead:
      enabling a tier changes nothing until the distributor flips this.

      TWO TEXT FIELDS, because a logo can BE a wordmark. Jake's first live
      test showed "Powered by GTC [GTC-LOGO]" -- the brand twice. The
      plain field is used where no image can render (email plain text, and
      when no logo is uploaded); the {logo} field is used where one can.

      PLUS A LINK URL (Unit 10, 2026-09-20). Jake: "The 'Powered by
      {logo}' footer should be a link to open in a new tab/window. We
      need a field on the branding page in the store editor under
      White-label branding for entering the URL." It is OPTIONAL and
      lives inside this same tier-gated card, so it appears and
      disappears with the entitlement exactly as the fields above do --
      never as a dead control on an unentitled store.
    -->
    <editor-card
      v-if="whiteLabelEnabled"
      title="White-label branding"
      description="Replace the ThreadFlow mark on this store with your own."
      data-testid="ct-branding-white-label-card"
    >
      <!--
        THE TOGGLE. Off by default for every store, including any that
        already uploaded assets under Units 1-6, so nothing changes
        appearance until the distributor asks for it.
      -->
      <div class="branding-field mb-4">
        <label class="branding-field__label">
          <input
            type="checkbox"
            :checked="whiteLabelOn"
            :disabled="!editMode"
            data-testid="ct-branding-white-label-toggle"
            @change="onWhiteLabelOnToggle"
          />
          Use my branding on this store
        </label>
        <p class="branding-card__hint">
          When this is off, the store shows the ThreadFlow mark. When it
          is on, the ThreadFlow mark is never shown &mdash; your logo and
          text are used instead.
        </p>
      </div>

      <div class="branding-identity">
        <!-- LEFT: the white-label logo. -->
        <div class="branding-identity__col">
          <label class="branding-field__label">White-label logo</label>
          <p
            class="branding-card__hint"
            data-testid="ct-branding-white-label-logo-hint"
          >
            Shown in place of the ThreadFlow logo on this store's
            storefront and emails. If no logo is uploaded, your
            "Powered by" text is shown on its own.
          </p>
          <image-uploader
            testid-prefix="ct-branding-white-label-logo"
            :current-image="whiteLabelLogoUrl"
            :disabled="!editMode"
            :uploading="whiteLabelLogoUploading"
            title="Add a white-label logo"
            hint="PNG or SVG, transparent background preferred."
            @upload="onWhiteLabelLogoUpload"
            @remove="onWhiteLabelLogoRemove"
            @error="onWhiteLabelLogoError"
          />

          <!--
            THE BROWSER-TAB FAVICON (Unit 12, 2026-09-22). Jake: "For the
            AdventHealth store, please pull the real AdventHealth favicon
            from https://www.adventhealth.com/ instead of using the full
            brand image. We probably need to add another field to the
            whitelabel branding section."

            A SEPARATE FIELD FROM THE LOGO ABOVE, because a favicon is a
            different ASSET and not a smaller rendering of one. A logo is
            usually a wordmark; a wordmark squeezed into a 16x16 tab icon
            is unreadable. AdventHealth publish a four-leaf pinwheel mark
            as their own favicon -- square, legible at tab size, and
            visibly not their wordmark.

            OPTIONAL. Left empty, the tab falls back to the store's brand
            image exactly as it does today, and failing that to the
            ThreadFlow icon -- so this field is a pure opt-in and no
            existing store's tab changes when it ships.

            NO SQUARE/SIZE VALIDATION, deliberately (Jake chose the
            plain-upload option over a validating one). A non-square image
            renders letterboxed rather than broken, and a validator would
            reject artwork a distributor had already decided to use.

            SAME UPLOAD PATH as the logo -- the shared ImageUploader and
            POST /upload_image. The one thing to keep straight is WHICH
            COLUMN each writes; see onWhiteLabelFaviconUpload.
          -->
          <label class="branding-field__label mt-4">Browser-tab icon</label>
          <p
            class="branding-card__hint"
            data-testid="ct-branding-white-label-favicon-hint"
          >
            Shown in the browser tab for this store. Use a square image
            &mdash; a wordmark is hard to read at this size. If none is
            uploaded, your logo is used.
          </p>
          <image-uploader
            testid-prefix="ct-branding-white-label-favicon"
            :current-image="whiteLabelFaviconUrl"
            :disabled="!editMode"
            :uploading="whiteLabelFaviconUploading"
            title="Add a browser-tab icon"
            hint="Square PNG or SVG, 32x32 or larger."
            @upload="onWhiteLabelFaviconUpload"
            @remove="onWhiteLabelFaviconRemove"
            @error="onWhiteLabelFaviconError"
          />
        </div>

        <!--
          RIGHT: the "Powered by" attribution line.

          A SEPARATE FIELD, not a deletion (Jake, 2026-09-19: "Add a field
          to the branding page for 'Powered by' style attribution -- for AH
          it will be 'Powered by TSG'"). The line is REPLACED with
          distributor-authored text rather than dropped.

          SECURITY: this is free text that reaches a shopper-facing page
          and outbound email. It stays an input VALUE here, which is inert
          by construction -- never v-html. The backend validator
          (_normalize_white_label_attribution) refuses control characters
          and over-length values at the write boundary; escaping happens
          once at each render sink. ``maxlength`` below is FEEDBACK, not
          enforcement: the server DROPS an over-length value rather than
          erroring, so without a visible cap a distributor would type 200
          characters, save, and watch the field silently revert on reload.
        -->
        <div class="branding-identity__col">
          <label class="branding-field__label">"Powered by" text</label>
          <p class="branding-card__hint">
            Used where your logo cannot be shown &mdash; plain-text
            emails, and anywhere you have not uploaded a logo.
          </p>
          <input
            type="text"
            class="branding-pms__freetext-input"
            :value="whiteLabelAttribution"
            :disabled="!editMode"
            :maxlength="WHITE_LABEL_ATTRIBUTION_MAX_LENGTH"
            placeholder="Powered by Your Company"
            data-testid="ct-branding-white-label-attribution"
            @input="onWhiteLabelAttributionInput"
          />

          <!--
            The {logo} form. OPTIONAL by design (Jake: "Logo alone if
            there is one, text-only attribution if there isn't a logo"),
            so a distributor whose logo is a complete wordmark leaves this
            blank and gets the bare image.
          -->
          <label class="branding-field__label mt-4">
            "Powered by" text with your logo
          </label>
          <p class="branding-card__hint">
            Used where your logo can be shown. Put
            <code>{logo}</code> where the logo should appear, or leave
            this blank to show the logo on its own.
          </p>
          <input
            type="text"
            class="branding-pms__freetext-input"
            :value="whiteLabelMarkText"
            :disabled="!editMode"
            :maxlength="WHITE_LABEL_ATTRIBUTION_MAX_LENGTH"
            placeholder="Powered by {logo}"
            data-testid="ct-branding-white-label-mark"
            @input="onWhiteLabelMarkInput"
          />

          <!--
            THE LINK URL (Unit 10). Jake, 2026-09-20: "The 'Powered by
            {logo}' footer should be a link to open in a new tab/window.
            We need a field on the branding page in the store editor
            under White-label branding for entering the URL."

            OPTIONAL. Left empty, the mark renders exactly as Unit 3
            shipped it -- plain, no anchor. It never produces an empty or
            "#" href: a dead link looks clickable, does nothing, and
            gives a shopper no way to tell it from a broken site.

            SECURITY: unlike the two text fields above, THIS ONE BECOMES
            AN href, which is the one case where escape-at-the-sink does
            not help -- an escaped `javascript:` URL is still a
            `javascript:` URL when a browser follows it. Two independent
            gates gate it: the backend validator
            (_normalize_white_label_link_url -- absolute http(s) only,
            host required, no control characters) keeps junk out of the
            column, and the render sink re-sanitizes through
            sanitizeExternalUrl, the same helper this footer already
            applies to the Privacy and Refund policy links beside the
            mark. `type="url"` below is FEEDBACK, not enforcement -- the
            column is craftable straight through the API, which is
            exactly why the server validates too.
          -->
          <label class="branding-field__label mt-4">
            "Powered by" link
          </label>
          <p class="branding-card__hint">
            Optional. Where the mark should link to &mdash; your own
            site. Opens in a new tab. Leave blank for no link.
          </p>
          <input
            type="url"
            class="branding-pms__freetext-input"
            :value="whiteLabelLinkUrl"
            :disabled="!editMode"
            :maxlength="WHITE_LABEL_LINK_URL_MAX_LENGTH"
            placeholder="https://yourcompany.com"
            data-testid="ct-branding-white-label-link-url"
            @input="onWhiteLabelLinkUrlInput"
          />

          <!--
            EMAIL SENDER IDENTITY (#3685). Jake, 2026-09-21: "Ideally this
            should be part of the white-label branding settings in the
            store editor -- both the display name and the email sender
            address."

            These are NOT new columns. They are the SAME
            `notification_from_name` / `notification_reply_to_email` the
            Email tab has always written, surfaced here because this is
            where a distributor configures how their brand appears. On a
            separate tab they were undiscoverable, which is why the live
            AdventHealth store shipped with both blank while its
            storefront branding was fully configured.

            Bound to the same campaign props and emitting the same
            `campaign-changed`, so both surfaces read and write ONE value
            and cannot disagree (single-source state,
            docs/design-philosophy.md).
          -->
          <label class="branding-field__label mt-4">Send emails as</label>
          <p class="branding-card__hint">
            The name shoppers see in their inbox on order confirmations.
            Leave blank to use your company name.
          </p>
          <input
            type="text"
            class="branding-pms__freetext-input"
            :value="notificationFromName"
            :disabled="!editMode"
            maxlength="255"
            placeholder="Your Store Name"
            data-testid="ct-branding-white-label-from-name"
            @input="onNotificationFromNameInput"
          />

          <!--
            THE FROM ADDRESS (2026-09-23). Jake: "if we already have
            notification_from_email and notification_reply_to_email, we
            don't need a toggle at all. Just use either/both field if
            non-empty, and if empty do what we would do anyway."

            NOT A NEW COLUMN, AND NOT A NEW BEHAVIOUR. Everything behind
            this input already existed -- the column, the permitted-write
            list, the write path, the loader read-back, and the send path
            that consults it. The ONLY thing missing was the input, which
            is why every store kept sending as the platform address: a
            distributor had no way to enter an alternative.

            NO TOGGLE BESIDE IT, deliberately. The two nullable columns
            already encode the state space -- set or not, each
            independently -- so a mode control would be a second source
            of truth that can disagree with the addresses it describes
            (single-source state, docs/design-philosophy.md).
          -->
          <label class="branding-field__label mt-4">Send from address</label>
          <p class="branding-card__hint">
            Optional. The address order emails are sent from. Needs DNS
            set up on that domain first (see below). Leave blank and
            emails come from us.
          </p>
          <input
            type="email"
            class="branding-pms__freetext-input"
            :value="notificationFromEmail"
            :disabled="!editMode"
            maxlength="255"
            placeholder="orders@yourcompany.com"
            data-testid="ct-branding-white-label-from-email"
            @input="onNotificationFromEmailInput"
          />

          <!--
            THE PENDING-VERIFICATION NOTICE. Required, not polish: an
            unverified From address falls back to the platform sender,
            and doing that SILENTLY is the same brand leak Jake reported
            -- just harder to notice, because the field looks configured.

            Shown only when an address is actually entered. An unverified
            EMPTY domain is the default state of every store, and
            warning about it would teach distributors to ignore the
            notice.
          -->
          <p
            v-if="fromDomainPendingVerification"
            class="branding-card__hint mt-2"
            data-testid="ct-branding-white-label-from-unverified"
          >
            <strong>This domain is not verified yet.</strong>
            Until the DNS records below are published and checked, order
            emails keep coming from our address with your name on them.
            Nothing breaks meanwhile.
          </p>

          <label class="branding-field__label mt-4">Reply-to address</label>
          <p class="branding-card__hint">
            Optional. Where shopper replies go. Leave blank and replies
            come to us. Independent of the address above -- you can set
            either, both, or neither.
          </p>
          <input
            type="email"
            class="branding-pms__freetext-input"
            :value="notificationReplyTo"
            :disabled="!editMode"
            maxlength="255"
            placeholder="support@yourcompany.com"
            data-testid="ct-branding-white-label-reply-to"
            @input="onNotificationReplyToInput"
          />

          <!--
            THE DNS NOTE. Jake, 2026-09-21: "The UI should include notes
            about the technical config (DNS) that needs to be done to make
            this work."

            Written for a distributor, not an engineer: what must be
            arranged, who arranges it, and -- critically -- what happens
            MEANWHILE, because the honest answer is "nothing breaks".

            REWRITTEN 2026-09-23 BECAUSE THE FIELD ABOVE MADE IT FALSE.
            It used to say the sending address "is ours by design" and
            that order emails "are sent from our address" -- true while
            there was no way to enter a From address, and wrong the
            moment one shipped. Stale prose beside a new control is its
            own defect (#2816 fixed an already-shipped article that went
            stale exactly this way), so this note changes with the
            surface rather than after someone notices.
          -->
          <p
            class="branding-card__hint mt-4"
            data-testid="ct-branding-white-label-email-dns-note"
          >
            <strong>About the sending address.</strong>
            Sending from an address at your own domain needs DNS records
            published on that domain by whoever manages it, so email
            providers can confirm the mail is really yours. Until that is
            arranged and verified, order emails come from our address
            showing the name above, and replies go wherever you point
            them -- so nothing breaks meanwhile.
          </p>
        </div>
      </div>
    </editor-card>

    <!--
      STOREFRONT LINK DOMAIN -- SUPERADMIN ONLY (2026-09-28).

      The host every emailed order link, {store_url} and product QR code
      for THIS store points at. Jake: the link base is PER STORE. With one
      platform-wide setting, pointing it at the AdventHealth store's domain
      sent every other store's order links there too.

      SuperAdmin only, because it is domain config that has to match DNS,
      SES and CORS -- a distributor who could set it could point their
      emailed links at a host nobody serves. Hiding it is UX; the boundary
      is the SA endpoint, which 404s anyone else.

      NOT inside the white-label card: that card is gated on the tier
      entitlement, and a store's link domain is independent of it.

      Saves through its own endpoint rather than the store autosave,
      because `update_campaign` deliberately refuses this column.
    -->
    <editor-card
      v-if="isSuperAdmin && storeId"
      title="Storefront link domain"
      description="SuperAdmin only. Where this store's emailed links and QR codes point."
      data-testid="ct-branding-link-base-card"
    >
      <div class="branding-field">
        <label class="branding-field__label" for="ct-branding-link-base-input">
          Link template
        </label>
        <p class="branding-card__hint">
          A full <code>https://</code> address containing <code>{slug}</code>, for example
          <code>https://shop.example.com/shop/{slug}</code>. Leave blank to
          use the platform setting. The domain must already serve this
          store.
        </p>
        <input
          id="ct-branding-link-base-input"
          v-model="linkBaseDraft"
          type="url"
          class="branding-pms__freetext-input"
          :disabled="!editMode || linkBaseSaving"
          placeholder="https://shop.example.com/shop/{slug}"
          autocomplete="off"
          spellcheck="false"
          data-testid="ct-branding-link-base-input"
          @input="linkBaseSaved = false"
        />
        <div class="mt-2">
          <button
            type="button"
            class="branding-pms__freetext-toggle"
            :disabled="!editMode || linkBaseSaving"
            data-testid="ct-branding-link-base-save"
            @click="saveLinkBase"
          >
            Save link domain
          </button>
        </div>
        <p
          v-if="linkBaseError"
          class="branding-card__hint mt-2"
          data-testid="ct-branding-link-base-error"
        >
          <strong>{{ linkBaseError }}</strong>
        </p>
        <p
          v-else-if="linkBaseSaved"
          class="branding-card__hint mt-2"
          data-testid="ct-branding-link-base-saved"
        >
          Saved. New emails and QR codes use this address.
        </p>
      </div>
    </editor-card>

    <!--
      3. Welcome message card (MOVED FROM PAYMENT & CREDITS)

      The message is authored here in a CKEditor5 rich-text field (same
      pattern as StoreMessage.vue) and persists to the store's `message`
      column as HTML. The storefront renders it via v-html
      (StorefrontViewShopper.vue) and the editor preview does too
      (StorefrontView.vue).

      SECURITY (stored-XSS, CWE-79): campaign.message is distributor-
      authored HTML. CKEditor limits editor INPUT, but the persisted
      column is craftable directly via the API, so both v-html RENDER
      sinks sanitize the value through @/utils/sanitizeHtml (DOMPurify
      allowlist) before display. Do NOT add a new render sink that
      v-html's the raw column -- route it through sanitizeMessageHtml.
    -->
    <editor-card
      title="Welcome message"
      description="A short greeting shown to shoppers on your storefront home page."
    >
      <!--
        Placement copy. The order stated here matches how HomePage.vue
        actually renders the home page: StorefrontHero (carousel) ->
        optional category banner -> the message (sf-home-message) ->
        StorefrontProductGrid. So the message lands below the carousel and
        above the products.
      -->
      <p
        class="branding-card__hint"
        data-testid="ct-branding-message-placement"
      >
        This appears on your storefront home page <strong>below the
        carousel</strong> and <strong>above the products</strong>.
      </p>
      <p
        class="branding-card__hint"
        data-testid="ct-branding-message-hint"
      >
        Use the toolbar to format your welcome message -- bold, italics,
        links, and lists.
      </p>
      <div
        class="branding-welcome-editor"
        data-testid="ct-branding-message-editor"
      >
        <rich-text-editor
          v-model="welcomeMessage"
          :read-only="!editMode"
          data-testid="branding-welcome-editor"
        />
      </div>
    </editor-card>

    <!--
      4. Product logos. Not tier-gated -- every store can choose both.

      "Shoppers choose one logo per product" (Jake, 2026-10-21;
      `placements_are_logo_choices`, default off). Off: a product's
      placements all apply together -- a left chest logo AND a back logo --
      so the storefront shows no logo picker, and every placement is ordered
      and priced. On: the placements are alternative logos and the shopper
      picks one (AdventHealth).

      "Show the logo when a product has only one" (Jake, 2026-10-07, a
      temporary workaround). With logo choice on, a product offering exactly
      ONE logo is no choice, and with this off (the default) the product
      page hides that logo and the cart does not name it. Display only: the
      order still records the logo.
    -->
    <editor-card
      title="Product logos"
      description="How a product's logos are offered to shoppers."
      data-testid="ct-branding-product-logos-card"
    >
      <div class="branding-field">
        <label class="branding-field__label">
          <input
            type="checkbox"
            :checked="logoChoices"
            :disabled="!editMode"
            data-testid="ct-branding-logo-choices-toggle"
            @change="onLogoChoicesToggle"
          />
          Shoppers choose one logo per product
        </label>
        <p class="branding-card__hint">
          Off: every logo on a product is applied, and shoppers do not pick.
          On: each logo on a product is an alternative, and shoppers pick one.
        </p>
      </div>
      <div class="branding-field">
        <label class="branding-field__label">
          <input
            type="checkbox"
            :checked="showSingleLogo"
            :disabled="!editMode"
            data-testid="ct-branding-show-single-logo-toggle"
            @change="onShowSingleLogoToggle"
          />
          Show the logo when a product has only one
        </label>
        <p class="branding-card__hint">
          When shoppers choose a logo, products with more than one always let
          them pick.
        </p>
      </div>
    </editor-card>
  </section>
</template>

<script>
import { describeError } from "@/utils/safeErrorLog";
import RichTextEditor from "@/components/RichTextEditor.vue";
import EditorCard from "./components/EditorCard.vue";
import HeroCarouselEditor from "./components/HeroCarouselEditor.vue";
import ImageUploader from "@/components/ImageUploader.vue";
import { getImageBaseUrl } from "@/utils/imageBaseUrl";
import {
  ARTWORK_UPLOAD_ERROR_EMPTY,
  isEmptyFile,
} from "@/utils/artworkUploadContract";
import {
  searchShades,
  lookupHex,
  normalizePmsCode,
  isValidPmsCode,
  PMS_CODE_MAX_LENGTH,
} from "@/constants/pmsPalette";
import { normalizeHex, isValidBrandHex } from "@/constants/brandColor";

// Hard cap on carousel slides per store. The "Add image" tile disables
// at this count; the backend will reject further inserts (E1 PR
// enforces this at the endpoint).
const MAX_CAROUSEL_SLIDES = 5;

// Strip the raw-HTML "empty paragraph" cruft that the legacy WYSIWYG
// editor used to leave in the message column (e.g. "<p>&nbsp;</p>"),
// so it doesn't render as visible noise in the plain-text editor. We
// only clean the value for DISPLAY in the textarea; the persisted value
// flows through onStoreMessageInput unchanged once the user edits.
//
// Scope is deliberately narrow: bare <p>...</p> with only whitespace /
// &nbsp; inside, no attributes. The legacy editor emitted exactly this
// shape for "empty" messages. Attributed paragraphs (<p class=...>) are
// NOT matched -- and a value that is empty cruft FOLLOWED by real text
// is preserved verbatim (see storeMessage computed): we only blank the
// field when the ENTIRE value is empty-paragraph cruft, to avoid
// silently mangling mixed content the distributor may have authored.
const EMPTY_HTML_PARAGRAPH_RE = /<p>(\s|&nbsp;|&#160;)*<\/p>/gi;

export default {
  name: "StoreBranding",

  components: {
    "rich-text-editor": RichTextEditor,
    EditorCard,
    HeroCarouselEditor,
    ImageUploader,
  },

  props: {
    campaign: { type: Object, default: () => ({}) },
    editMode: { type: Boolean, default: false },
  },

  emits: ["campaign-changed", "update-tab-invalid"],

  data() {
    return {
      // Hero-carousel auto-advance slider bounds (seconds). Exposed on the
      // instance so the template can render the min/max end labels and the
      // computed clamp can reuse them (single source of truth).
      CAROUSEL_INTERVAL_MIN: 2,
      CAROUSEL_INTERVAL_MAX: 15,

      // E2: Hero carousel state. Slides are loaded on mount from
      // GET /api/stores/<id>/carousel_slides and mutated via
      // POST/PATCH/DELETE.
      carouselSlides: [],
      selectedSlideId: null,

      // Natural pixel dimensions of each slide thumbnail, keyed by slide id:
      // { [id]: { width, height } }. Read client-side from the loaded image's
      // naturalWidth/naturalHeight via @load -- no backend/schema change, so
      // it works for already-uploaded images too. Rendered as a caption under
      // each thumbnail.
      slideDims: {},

      // Brand-image upload-in-flight flag, driven into the ImageUploader's
      // `uploading` prop while the POST /upload_image round-trips.
      brandImageUploading: false,

      // Same flag for the SECOND uploader on this page, the white-label
      // logo. Deliberately its own field rather than a shared one: the
      // two uploaders sit side by side, and a shared flag would spin the
      // brand-image dropzone while the white-label upload was in flight.
      whiteLabelLogoUploading: false,
      // Unit 12: upload-in-flight flag for the browser-tab favicon, driven
      // into its ImageUploader's `uploading` prop. A SEPARATE flag from the
      // logo's, or uploading one would spin the other's dropzone.
      whiteLabelFaviconUploading: false,

      // Mirrors the backend bound in
      // ``_normalize_white_label_attribution`` (api/src/models/store.py).
      // Kept as a named constant rather than a literal in the template so
      // the two can be compared by anyone auditing the pair. The server
      // is the real guard -- this is what makes the bound visible while
      // typing, since an over-length value is DROPPED server-side rather
      // than rejected, which without feedback looks like a silent revert.
      WHITE_LABEL_ATTRIBUTION_MAX_LENGTH: 128,

      // Mirrors ``WHITE_LABEL_LINK_URL_MAX_LENGTH`` in
      // api/src/models/store.py. Larger than the attribution bound
      // because this is a URL -- a legitimate one carrying a path and UTM
      // parameters runs long where an attribution LINE does not. Same
      // feedback-not-enforcement role as the constant above: the server
      // DROPS an over-length value, so without a visible cap a
      // distributor would paste a long URL, save, and watch the field
      // silently revert on reload.
      WHITE_LABEL_LINK_URL_MAX_LENGTH: 512,

      // --- PMS (Pantone) brand-color picker state ---------------------
      // Search box contents. Purely local: the picker filters the
      // vendored palette in memory (576 entries), no network call.
      pmsQuery: "",
      // Whether the "not listed?" free-text escape is expanded.
      freeTextOpen: false,
      // In-progress free-text code (committed on change, not per
      // keystroke -- validating mid-typing would flash errors at a user
      // who is not finished).
      freeTextCode: "",
      // In-progress user-supplied hex for an off-list code.
      freeTextHex: "",
      // True once a committed free-text code has no known approximation,
      // which is what reveals the hex input.
      freeTextNeedsHex: false,
      // Inline validation message; null when the input is acceptable.
      freeTextError: null,
      // Cap on rendered rows. The palette is 576 entries and an empty
      // query would otherwise paint every one of them on each keystroke.
      PMS_MAX_VISIBLE: 40,

      // E3: drag-reorder state. Set by dragstart, cleared on dragend.
      // Used by onDrop to identify the slide being moved without
      // depending on the synthetic DataTransfer object (which is
      // awkward to populate in mount tests).
      draggedSlideId: null,

      // SuperAdmin-only storefront link domain. Its own draft/saving state:
      // it saves through the SA endpoint, never the store autosave.
      linkBaseDraft: "",
      linkBaseSaving: false,
      linkBaseSaved: false,
      linkBaseError: "",
    };
  },

  computed: {
    // The SAME columns the Email tab binds -- surfaced here, not copied.
    notificationFromName() {
      return this.campaign?.notification_from_name || "";
    },
    notificationReplyTo() {
      return this.campaign?.notification_reply_to_email || "";
    },

    // The store's own From ADDRESS. Bound to the value the LOADER
    // returned (campaign.py already selects this column), because a field
    // that renders blank over a stored value gets that blank written back
    // by the next save of any unrelated field -- #2881.
    notificationFromEmail() {
      return this.campaign?.notification_from_email || "";
    },

    // Whether to warn that the From address cannot take effect yet.
    //
    // Only when an address is actually entered: an unverified EMPTY
    // domain is the default for every store, not a misconfiguration.
    // Keyed off the FROM column alone -- verification governs what we may
    // send AS, and nothing is signed on behalf of a Reply-To, so an
    // unverified reply-to domain is fine and must not warn.
    //
    // `sender_domain_verified` is reported by the backend. A MISSING flag
    // is treated as not-verified, matching the send path's fail-closed
    // posture: absence is not permission.
    fromDomainPendingVerification() {
      if (!this.notificationFromEmail) return false;
      return this.campaign?.sender_domain_verified !== true;
    },
    brandColor() {
      return this.campaign?.brand_color || "";
    },

    // The store's currently-saved PMS code ("" when never set). Read
    // straight off the campaign so reopening the editor shows what was
    // persisted, the same way brandColor does.
    brandPmsCode() {
      return this.campaign?.brand_pms_code || "";
    },

    // Shades matching the current query, capped for render cost. An
    // empty query renders nothing (the list only appears once the user
    // starts typing), so the card stays compact.
    visibleShades() {
      if (!this.pmsQuery) return [];
      return searchShades(this.pmsQuery).slice(0, this.PMS_MAX_VISIBLE);
    },

    // Resolved URL for the brand image, fed to the shared ImageUploader's
    // `currentImage` prop (null -> empty dropzone). Mirrors how the email
    // header image resolves its stored key (StoreEmailTemplates.vue):
    // a stored key is a bare S3 object key, so anything carrying a scheme
    // separator ("://") -- e.g. a legacy seed placeholder or a raw URL --
    // is treated as unresolvable and shows the empty state.
    brandImageUrl() {
      const key = this.campaign?.brand_image || null;
      if (!key || String(key).includes("://")) return null;
      return `${getImageBaseUrl()}${key}`;
    },

    // --------------------------------------------------------------
    // White-label branding (Unit 5)
    // --------------------------------------------------------------

    // Whether this store's DISTRIBUTOR holds the white-label entitlement.
    //
    // The value comes from ``campaign.white_label.enabled`` -- the answer
    // ``services/white_label.white_label_for_store`` computed server-side
    // and ``getCampaignById`` serialized onto this payload. The component
    // does not and must not derive it: the entitlement lives on the
    // distributor's subscription TIER, which the browser has no other
    // route to.
    //
    // ``=== true`` rather than a truthy check, deliberately. A payload
    // that predates Unit 1, a cached response, or a mock that omits the
    // key yields ``undefined``, and deny-by-default is the correct
    // reading of "we could not establish an entitlement" -- the cost of
    // wrongly denying is a store that looks exactly like every store does
    // today, while the cost of wrongly granting is a paid feature handed
    // out invisibly.
    whiteLabelEnabled() {
      return this.campaign?.white_label?.enabled === true;
    },

    // Resolved URL for the white-label logo, fed to its ImageUploader's
    // `currentImage` prop. Same resolution rule as ``brandImageUrl``
    // above (a stored value is a bare object key, so anything carrying a
    // scheme separator is unresolvable and shows the empty state) -- and
    // a DIFFERENT column, because the two marks are different assets on
    // different surfaces.
    whiteLabelLogoUrl() {
      const key = this.campaign?.white_label_logo || null;
      if (!key || String(key).includes("://")) return null;
      return `${getImageBaseUrl()}${key}`;
    },

    // Resolved URL for the browser-tab favicon (Unit 12), fed to its own
    // ImageUploader's `currentImage` prop. Same resolution rule as the
    // logo directly above -- a stored value is a bare object key, so
    // anything carrying a scheme separator is unresolvable and shows the
    // empty state rather than a broken image -- and a DIFFERENT column,
    // because a tab icon and a footer mark are different assets.
    whiteLabelFaviconUrl() {
      const key = this.campaign?.white_label_favicon || null;
      if (!key || String(key).includes("://")) return null;
      return `${getImageBaseUrl()}${key}`;
    },

    // The stored attribution line, or "" so the input is controlled and
    // the PLACEHOLDER shows through. Returning the default string itself
    // would be wrong twice over: it would persist "Powered by ThreadFlow"
    // as an explicit value on the first save of any other field, and it
    // would hide the fact that the field is unset.
    whiteLabelAttribution() {
      return this.campaign?.white_label_attribution || "";
    },

    // The {logo}-placeholder form. Same "" default and the same reason:
    // a controlled input needs a string, and defaulting to the
    // placeholder text would PERSIST it on the first keystroke, turning a
    // hint into stored data nobody typed.
    whiteLabelMarkText() {
      return this.campaign?.white_label_mark || "";
    },

    // The brand link URL (Unit 10). Same "" default as the two text
    // fields, for the same controlled-input reason.
    //
    // READ FROM THE FLAT COLUMN, not from campaign.white_label.link_url.
    // Those are two different things and the distinction is load-bearing
    // on this page: `campaign.white_label` is the RESOLVER's answer
    // (tier-gated, computed server-side, read-only), while the flat
    // `white_label_link_url` is the store's own column that the editor
    // round-trips and PATCHes back. Reading the resolver's copy here
    // would make the field render a value it cannot save. The sibling
    // fields above already take the flat columns -- this matches them.
    whiteLabelLinkUrl() {
      return this.campaign?.white_label_link_url || "";
    },

    // The per-store toggle. A REAL boolean test, not truthiness: the
    // column is NOT NULL, but a payload predating white_label_002 has no
    // key at all, and "off" is the correct read for that -- it renders
    // today's ThreadFlow mark, which is never a broken state.
    whiteLabelOn() {
      return this.campaign?.white_label_on === true;
    },

    // Literal-true only, like `whiteLabelOn`: an absent key (a payload
    // predating store_show_single_logo_001) reads as the default, off.
    showSingleLogo() {
      return this.campaign?.show_single_logo === true;
    },

    // Literal-true only, like `showSingleLogo`: an absent key (a payload
    // predating store_logo_choices_001) reads as the default, off.
    logoChoices() {
      return this.campaign?.placements_are_logo_choices === true;
    },

    // Hero-carousel auto-advance interval, presented to the slider in
    // SECONDS (the persisted column is milliseconds). Falls back to 5s
    // (the store default) when the campaign has no stored value yet.
    // Clamped to the slider's 2-15s range so a legacy/out-of-range
    // stored value still renders the thumb inside the track.
    carouselIntervalSeconds() {
      const ms = Number(this.campaign?.hero_carousel_interval_ms);
      const seconds = Number.isFinite(ms) && ms > 0 ? Math.round(ms / 1000) : 5;
      return Math.min(
        this.CAROUSEL_INTERVAL_MAX,
        Math.max(this.CAROUSEL_INTERVAL_MIN, seconds)
      );
    },
    storeMessage() {
      // Present a clean value: drop the legacy empty-paragraph HTML
      // cruft so the editor isn't seeded with "<p>&nbsp;</p>".
      const raw = this.campaign?.message || "";
      return raw.replace(EMPTY_HTML_PARAGRAPH_RE, "").trim() === ""
        ? ""
        : raw;
    },

    // v-model target for the Welcome-message rich-text editor. The getter
    // presents the cleaned message (via storeMessage); the setter mutates the
    // persisted `message` column in place and emits @campaign-changed --
    // the SAME pattern the former textarea used in onStoreMessageInput, so
    // the parent (StoreAddEdit.vue) autosave picks the change up. Editing
    // is gated in the template via :read-only="!editMode".
    welcomeMessage: {
      get() {
        return this.storeMessage;
      },
      set(value) {
        // Real, persisted column (api/src/models/store.py "message").
        // Mutate in place so the parent autosave catches it via
        // @campaign-changed -- same as Brand Color / Brand Image.
        this.campaign.message = value;
        this.$emit("campaign-changed");
      },
    },

    // Render order is display_order ascending. Falls back to the
    // existing array order for slides that happen to share a value.
    orderedSlides() {
      const copy = [...this.carouselSlides];
      copy.sort((a, b) => {
        const da = Number.isFinite(a.display_order) ? a.display_order : 0;
        const db = Number.isFinite(b.display_order) ? b.display_order : 0;
        return da - db;
      });
      return copy;
    },

    addDisabled() {
      return this.carouselSlides.length >= MAX_CAROUSEL_SLIDES;
    },

    selectedSlide() {
      if (this.selectedSlideId == null) return null;
      return (
        this.carouselSlides.find(
          (s) => String(s.id) === String(this.selectedSlideId),
        ) || null
      );
    },

    storeId() {
      return this.campaign?._jv?.id || this.campaign?.id || null;
    },

    // Role-name based, and still true while impersonating -- matching the
    // backend, where an impersonating SA keeps ``superadmin.settings``.
    isSuperAdmin() {
      return Boolean(this.$store && this.$store.getters?.isSuperAdmin);
    },

    linkBaseUrl() {
      return `/superadmin/stores/${this.storeId}/storefront-base-url-template`;
    },
  },

  mounted() {
    this.loadCarouselSlides();
    this.loadLinkBase();
  },

  methods: {
    // CRLF is stripped before it reaches the column: both values become
    // MAIL HEADERS, and a newline in a header is header injection. The
    // backend strips and validates too (_header_safe / _safe_reply_to in
    // plugins/mail.py); this is feedback, not the enforcement.
    onNotificationFromNameInput(event) {
      const value = (event?.target?.value ?? "").replace(/[\r\n]/g, "");
      this.campaign.notification_from_name = value || null;
      this.$emit("campaign-changed");
    },
    onNotificationReplyToInput(event) {
      const value = (event?.target?.value ?? "").replace(/[\r\n]/g, "");
      this.campaign.notification_reply_to_email = value || null;
      this.$emit("campaign-changed");
    },
    // Writes ONLY its own column. The two addresses are independent, so
    // this handler must not touch the reply-to beside it -- a distributor
    // clearing one must not lose the other.
    //
    // `value || null` stores NULL rather than "": the input clears to an
    // empty string, and NULL keeps the row honest about "never
    // configured". The backend treats both as empty either way.
    onNotificationFromEmailInput(event) {
      const value = (event?.target?.value ?? "").replace(/[\r\n]/g, "");
      this.campaign.notification_from_email = value || null;
      this.$emit("campaign-changed");
    },
    // validateTab() is the contract the parent calls via tabRef().
    // Branding has no required fields, so it's always valid.
    // IMPORTANT: do NOT emit update-tab-invalid here -- the parent's
    // updateTabInvalid handler calls back into safeValidateTab which
    // would re-enter validateTab and create an infinite loop. Just
    // return the validity boolean.
    validateTab() {
      return false;
    },

    // Save-time contract called by the parent (StoreAddEdit.vue) on the
    // active tab's ref during autosave (saveTab) and full save
    // (saveCampaign). Every sibling section exposes this.
    //
    // The branding-owned columns (brand_color, brand_image, message) are
    // edited in place on the campaign object via onBrandColorInput /
    // onBrandImageUpload+onBrandImageRemove / the welcomeMessage setter --
    // the parent passes the SAME campaign object as both this component's
    // `campaign` prop and this method's argument, so the edits are already
    // present on `campaign` by the time updateCampaign runs. We therefore
    // just return a fresh shallow copy (matching the sibling sections'
    // {...campaign, ...} return shape) without re-stamping the branding
    // fields. brand_image is edited here again (ADR 0058) and persists via
    // the campaign autosave allowlist. Carousel slides are NOT folded here
    // -- they persist directly against the carousel_slides endpoint as the
    // user edits them.
    updateCampaign(campaign) {
      return { ...(campaign || {}) };
    },

    // Lifecycle hook the parent invokes on the active tab's ref
    // (StoreAddEdit.vue tabRef(11).loadComponent()). Carousel slides are
    // already loaded in mounted(); nothing further to lazy-load here, so
    // this is a no-op that satisfies the section-component contract.
    loadComponent() {
      // no-op -- slides load in mounted()
    },

    // ----------------------------------------------------------------
    // PMS (Pantone) brand-color picker
    //
    // Two write paths land in the SAME two columns and must behave
    // identically:
    //   onSelectShade        -- palette pick; hex from our approximation.
    //   onFreeTextHexCommit  -- off-list code; hex from the distributor.
    // Both mutate the campaign in place + emit @campaign-changed, which
    // is the established section pattern the parent autosave listens to.
    // ----------------------------------------------------------------

    onPmsQueryInput(event) {
      this.pmsQuery = event.target.value;
    },

    // Pick a shade from the vendored palette: store the canonical code
    // AND its approximation hex.
    onSelectShade(shade) {
      if (!this.editMode || !shade) return;
      this.campaign.brand_pms_code = shade.code;
      this.campaign.brand_color = shade.hex;
      // Collapse the list -- the selection is now shown above it.
      this.pmsQuery = "";
      this.freeTextError = null;
      this.$emit("campaign-changed");
    },

    // Remove the PMS code. Deliberately leaves brand_color alone: the
    // storefront still needs a color to paint, and dropping the code is
    // not a statement about the on-screen color.
    onClearPmsCode() {
      if (!this.editMode) return;
      this.campaign.brand_pms_code = null;
      this.freeTextOpen = false;
      this.freeTextCode = "";
      this.freeTextNeedsHex = false;
      this.freeTextError = null;
      this.$emit("campaign-changed");
    },

    onToggleFreeText() {
      if (!this.editMode) return;
      this.freeTextOpen = !this.freeTextOpen;
      if (this.freeTextOpen) {
        // Seed from whatever is already saved so the escape hatch opens
        // showing the current state rather than an empty box.
        this.freeTextCode = this.brandPmsCode;
        this.freeTextHex = this.brandColor || "";
        this.freeTextError = null;
      }
    },

    onFreeTextCodeInput(event) {
      this.freeTextCode = event.target.value;
      // Clear a stale error as soon as the user edits; the verdict is
      // re-issued on commit.
      this.freeTextError = null;
    },

    // Commit the typed code. Validates BEFORE writing so a malformed
    // value never reaches the campaign (and therefore never reaches the
    // API). The backend re-validates independently -- this is the inline
    // error, not the security boundary.
    onFreeTextCodeCommit() {
      if (!this.editMode) return;

      const raw = this.freeTextCode;
      if (!raw || !raw.trim()) {
        // Empty means "no code" -- clear rather than error.
        this.campaign.brand_pms_code = null;
        this.freeTextNeedsHex = false;
        this.freeTextError = null;
        this.$emit("campaign-changed");
        return;
      }

      if (!isValidPmsCode(raw)) {
        this.freeTextError =
          `Enter a PMS code using letters, numbers and spaces ` +
          `(up to ${PMS_CODE_MAX_LENGTH} characters), for example ` +
          `"PMS 286 C".`;
        this.freeTextNeedsHex = false;
        return;
      }

      const canonical = normalizePmsCode(raw);
      this.campaign.brand_pms_code = canonical;
      this.freeTextCode = canonical;
      this.freeTextError = null;

      // If the typed code IS one we carry, use our approximation rather
      // than making the user supply a hex they would have to look up.
      const knownHex = lookupHex(canonical);
      if (knownHex) {
        this.campaign.brand_color = knownHex;
        this.freeTextHex = knownHex;
        this.freeTextNeedsHex = false;
      } else {
        // No approximation exists -- ask. Pre-filled with the store's
        // current color so a save can never blank the storefront accent.
        this.freeTextNeedsHex = true;
        this.freeTextHex = this.brandColor || "";
      }

      this.$emit("campaign-changed");
    },

    onFreeTextHexInput(event) {
      this.freeTextHex = event.target.value;
      this.freeTextError = null;
    },

    // Commit the user-supplied hex for an off-list code. Rejected input
    // is surfaced inline and NOT coerced to some fallback color -- a
    // silent coercion would paint the storefront a color the distributor
    // never chose.
    onFreeTextHexCommit() {
      if (!this.editMode) return;

      const normalized = normalizeHex((this.freeTextHex || "").trim());
      if (!isValidBrandHex(normalized)) {
        this.freeTextError =
          "Enter a 6-digit hex color such as #0033A0.";
        return;
      }

      this.campaign.brand_color = normalized;
      this.freeTextHex = normalized;
      this.freeTextError = null;
      this.$emit("campaign-changed");
    },

    onBrandColorInput(event) {
      // Mutate the prop in place -- this is the established pattern in
      // the other store-editor sections (StoreSetup, StoreCheckoutInfo,
      // etc.). The parent watches the campaign object and the autosave
      // listener catches @campaign-changed.
      this.campaign.brand_color = event.target.value;
      this.$emit("campaign-changed");
    },

    // --------------------------------------------------------------
    // Brand image (ADR 0058)
    //
    // The shared ImageUploader (frontend/src/components/ImageUploader.vue)
    // is fully controlled: it validates + emits the raw File and the
    // PARENT owns persistence. We mirror PR-4's email-header-image wiring
    // EXACTLY (StoreEmailTemplates.vue onHeaderUpload/onHeaderRemove):
    // POST /upload_image, then set the store column in place + emit
    // @campaign-changed so the parent (StoreAddEdit.vue) autosave catches
    // it. brand_image is already on the campaign autosave allowlist
    // (api/src/endpoints/campaign.py), so no new endpoint is introduced.
    // --------------------------------------------------------------
    async onBrandImageUpload(file) {
      if (!file || !this.$axios || !this.editMode) return;
      this.brandImageUploading = true;
      const form = new FormData();
      form.append("file", file);
      try {
        const resp = await this.$axios.post(`/upload_image`, form, {
        });
        const uuid = resp?.data?.uuid;
        if (uuid) {
          // Real, persisted store column -- mutate in place like the
          // sibling cards so the parent autosave picks it up.
          this.campaign.brand_image = uuid;
          this.$emit("campaign-changed");
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Failed to upload brand image", describeError(err));
      } finally {
        this.brandImageUploading = false;
      }
    },

    onBrandImageRemove() {
      if (!this.editMode) return;
      // Clear the stored key; the uploader returns to its empty dropzone
      // state via brandImageUrl -> null.
      this.campaign.brand_image = "";
      this.$emit("campaign-changed");
    },

    onBrandImageError(message) {
      // Client-side validation failure from the shared uploader (bad
      // type/size). Surface it through the store-level error toast, same
      // as the email header image (StoreEmailTemplates.vue).
      if (this.$store && this.$store.dispatch) {
        this.$store.dispatch("raiseError", message);
      }
    },

    // --------------------------------------------------------------
    // White-label branding (Unit 5, card mq26K91V)
    //
    // Persistence is the SAME path as the brand image directly above --
    // POST /upload_image, set the column in place, emit
    // @campaign-changed so the parent (StoreAddEdit.vue) autosave PATCHes
    // it through update_campaign. No new endpoint and no new mechanism.
    //
    // The one thing to keep straight is WHICH COLUMN each uploader
    // writes. They are adjacent, they share a component, and crossing
    // them would silently replace the storefront topbar logo -- a defect
    // that only shows up on the live storefront, well after the save
    // looked successful. ``StoreBranding.white-label.spec.js`` pins that
    // separation with a dedicated test rather than leaving it to review.
    // --------------------------------------------------------------
    async onWhiteLabelLogoUpload(file) {
      if (!file || !this.$axios || !this.editMode) return;
      this.whiteLabelLogoUploading = true;
      const form = new FormData();
      form.append("file", file);
      try {
        const resp = await this.$axios.post(`/upload_image`, form, {});
        const uuid = resp?.data?.uuid;
        if (uuid) {
          this.campaign.white_label_logo = uuid;
          this.$emit("campaign-changed");
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Failed to upload white-label logo", describeError(err));
      } finally {
        this.whiteLabelLogoUploading = false;
      }
    },

    onWhiteLabelLogoRemove() {
      if (!this.editMode) return;
      // Clearing is SAFE and needs no warning: Jake's rule 3 means a
      // white-labeled store with no logo falls back to the ThreadFlow
      // logo rather than rendering an empty mark. The hint beside the
      // field says so, which is why removing does not prompt.
      this.campaign.white_label_logo = "";
      this.$emit("campaign-changed");
    },

    onWhiteLabelLogoError(message) {
      if (this.$store && this.$store.dispatch) {
        this.$store.dispatch("raiseError", message);
      }
    },

    // --------------------------------------------------------------
    // The browser-tab favicon (Unit 12, card mq26K91V)
    //
    // Identical persistence to the white-label logo directly above --
    // POST /upload_image, set the column in place, emit
    // @campaign-changed so the parent autosave PATCHes it through
    // update_campaign. No new endpoint and no new mechanism.
    //
    // THE COLUMN IS THE ONLY THING THAT DIFFERS, and there are now THREE
    // uploaders on this page sharing one component (brand image,
    // white-label logo, white-label favicon). Crossing any two writes the
    // wrong column, the save returns 200, and the defect appears only on
    // the live storefront. ``StoreBranding.favicon.spec.js`` pins the
    // separation with a dedicated test rather than leaving it to review.
    // --------------------------------------------------------------
    async onWhiteLabelFaviconUpload(file) {
      if (!file || !this.$axios || !this.editMode) return;
      this.whiteLabelFaviconUploading = true;
      const form = new FormData();
      form.append("file", file);
      try {
        const resp = await this.$axios.post(`/upload_image`, form, {});
        const uuid = resp?.data?.uuid;
        if (uuid) {
          this.campaign.white_label_favicon = uuid;
          this.$emit("campaign-changed");
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Failed to upload white-label favicon", describeError(err));
      } finally {
        this.whiteLabelFaviconUploading = false;
      }
    },

    onWhiteLabelFaviconRemove() {
      if (!this.editMode) return;
      // Clearing is SAFE and needs no warning: the tab falls back to the
      // store's brand image, and failing that to the ThreadFlow icon. The
      // hint beside the field says so, which is why removing does not
      // prompt.
      //
      // Written as "" rather than null to match the logo and brand-image
      // remove paths -- update_campaign applies a column only under
      // `if "<col>" in nc`, so the key must be PRESENT to clear it.
      this.campaign.white_label_favicon = "";
      this.$emit("campaign-changed");
    },

    onWhiteLabelFaviconError(message) {
      if (this.$store && this.$store.dispatch) {
        this.$store.dispatch("raiseError", message);
      }
    },

    onWhiteLabelAttributionInput(event) {
      if (!this.editMode) return;
      // Per-keystroke, matching brand_color rather than the PMS free-text
      // field (which commits on change because it VALIDATES, and
      // validating mid-typing flashes errors at someone who is not
      // finished). There is nothing to validate here in the browser --
      // the server owns the rules -- so the parent's debounced autosave
      // is the only rate limiter needed.
      this.campaign.white_label_attribution = event.target.value;
      this.$emit("campaign-changed");
    },

    onWhiteLabelMarkInput(event) {
      if (!this.editMode) return;
      // Same per-keystroke shape as the plain field above. The {logo}
      // token is NOT validated here: the render sink treats a missing
      // token as "append the logo" and a doubled one as a single
      // substitution, so there is no malformed state to warn about, and
      // a validator would be inventing a rule the renderer does not have.
      this.campaign.white_label_mark = event.target.value;
      this.$emit("campaign-changed");
    },

    onWhiteLabelLinkUrlInput(event) {
      if (!this.editMode) return;
      // Per-keystroke like its two siblings, and deliberately NOT
      // validated in the browser. A URL is half-invalid for most of the
      // time it is being typed ("h", "ht", "https:/"), so validating on
      // input would flash an error at every distributor who is simply
      // not finished. The server owns the rule and DROPS a malformed
      // value, and the render sink refuses to make an unsafe one into an
      // anchor -- so the worst outcome of a bad value is no link, which
      // is the field's own empty state.
      this.campaign.white_label_link_url = event.target.value;
      this.$emit("campaign-changed");
    },

    onShowSingleLogoToggle(event) {
      if (!this.editMode) return;
      this.campaign.show_single_logo = event.target.checked === true;
      this.$emit("campaign-changed");
    },

    onLogoChoicesToggle(event) {
      if (!this.editMode) return;
      this.campaign.placements_are_logo_choices = event.target.checked === true;
      this.$emit("campaign-changed");
    },

    onWhiteLabelOnToggle(event) {
      if (!this.editMode) return;
      // Coerced from the checkbox's own boolean rather than toggling the
      // previous value, so a re-render mid-interaction cannot invert it.
      this.campaign.white_label_on = event.target.checked === true;
      this.$emit("campaign-changed");
    },

    // Auto-advance interval slider. Receives SECONDS from v-slider
    // (@update:modelValue -- @change does not fire on Vuetify 3 sliders),
    // converts to MILLISECONDS for the persisted column, and mutates the
    // campaign in place + emits @campaign-changed so the parent autosave
    // catches it -- the SAME pattern as brand_color / message. The parent
    // debounces the actual PATCH; the backend clamps the ms value to a
    // safe range, so no debounce is needed here.
    onCarouselIntervalInput(seconds) {
      const s = Number(seconds);
      if (!Number.isFinite(s)) return;
      this.campaign.hero_carousel_interval_ms = Math.round(s * 1000);
      this.$emit("campaign-changed");
    },

    // --------------------------------------------------------------
    // Hero carousel (E2)
    // --------------------------------------------------------------

    // Render URL for a slide's image. Uses the standard image base URL
    // resolution -- same path /api/upload_image stores reference.
    slideImageUrl(slide) {
      const base = getImageBaseUrl();
      const id = slide?.image_file_id;
      // Unresolvable image_file_id -> visible placeholder rather than a
      // broken thumbnail. Two unresolvable cases:
      //   (a) null / empty  -- no image assigned yet.
      //   (b) not a plain S3 key -- anything carrying a scheme separator
      //       ("://"), e.g. the legacy seed placeholder
      //       "stub://image/carousel-store-N.jpg", which is NOT a real
      //       bucket key and would render as a broken image.
      // A real key (bare uuid or "seed-<sha>.jpg" filename) has no "://"
      // and resolves normally.
      if (!id || String(id).includes("://")) {
        return `${base}default-product.svg`;
      }
      return `${base}${id}`;
    },

    // Record a thumbnail's natural pixel dimensions when its image loads.
    // Read client-side; no backend call. Replacing the whole object keeps the
    // caption reactive under Vue 3's plain-object reactivity.
    onThumbImageLoad(slideId, event) {
      const img = event && event.target;
      if (!img || !img.naturalWidth || !img.naturalHeight) {
        return;
      }
      this.slideDims = {
        ...this.slideDims,
        [slideId]: { width: img.naturalWidth, height: img.naturalHeight },
      };
    },

    // "{width} x {height} px" caption for a slide, or "" before its image
    // loads (the caption is v-if-gated so nothing renders meanwhile).
    thumbDimsLabel(slideId) {
      const dims = this.slideDims[slideId];
      if (!dims) {
        return "";
      }
      return `${dims.width} x ${dims.height} px`;
    },

    // Only a SuperAdmin fetches it: for anyone else the endpoint 404s, and
    // a request that is certain to fail is noise in the console.
    async loadLinkBase() {
      if (!this.isSuperAdmin || !this.storeId || !this.$axios) return;
      try {
        const resp = await this.$axios.get(this.linkBaseUrl);
        const value = resp?.data?.value;
        this.linkBaseDraft = typeof value === "string" ? value : "";
      } catch (err) {
        this.linkBaseError = "Could not load the link domain.";
      }
    },

    // Blank clears it (null), so the store falls back to the platform
    // setting. The server is the authority on shape; its validator is the
    // same one the platform setting uses.
    async saveLinkBase() {
      if (!this.storeId || !this.$axios) return;
      const trimmed = (this.linkBaseDraft || "").trim();
      const value = trimmed || null;
      this.linkBaseSaving = true;
      this.linkBaseSaved = false;
      this.linkBaseError = "";
      try {
        const resp = await this.$axios.patch(this.linkBaseUrl, { value });
        const saved = resp?.data?.value;
        this.linkBaseDraft = typeof saved === "string" ? saved : "";
        this.linkBaseSaved = true;
      } catch (err) {
        const status = err?.response?.status;
        this.linkBaseError =
          status === 400
            ? "That address was rejected. Use a full https:// address " +
              "containing {slug} exactly once, with no trailing slash."
            : status === 404
              ? "Only a SuperAdmin can change this."
              : "Could not save the link domain.";
      } finally {
        this.linkBaseSaving = false;
      }
    },

    async loadCarouselSlides() {
      if (!this.storeId || !this.$axios) return;
      try {
        const resp = await this.$axios.get(
          `/stores/${this.storeId}/carousel_slides`,
        );
        // SAFRS / JSON:API shape: { data: [{ id, type, attributes }] }
        // The upload endpoint returns the same shape.
        const rows = (resp?.data?.data || []).map((row) => ({
          id: row.id,
          ...(row.attributes || {}),
        }));
        this.carouselSlides = rows;
        // Auto-select the FIRST slide (display_order ascending) so its
        // inline editor opens immediately -- the headline/URL fields are
        // otherwise hidden until a thumbnail is clicked, with nothing
        // signalling they exist. With zero slides, leave nothing selected.
        if (this.selectedSlideId == null) {
          const first = this.orderedSlides[0];
          if (first != null) {
            this.selectedSlideId = String(first.id);
          }
        }
      } catch (err) {
        // Silent on read failure -- the card renders as empty. Logged
        // for diagnosis but not user-facing here; the StoreAddEdit
        // toast plumbing covers save errors.
        // eslint-disable-next-line no-console
        console.warn("Failed to load carousel slides", describeError(err));
      }
    },

    onThumbClick(slide) {
      this.selectedSlideId = String(slide.id);
    },

    onAddClick() {
      if (this.addDisabled || !this.editMode) return;
      const input = this.$refs.carouselFileInput;
      if (input) {
        input.value = "";
        input.click();
      }
    },

    async onFileSelected(event) {
      const file = event?.target?.files?.[0];
      if (!file || !this.storeId || !this.$axios) return;
      // Cloud-storage placeholder: right name, zero bytes. Never POST
      // it -- see utils/artworkUploadContract.js for the root cause.
      if (isEmptyFile(file)) {
        // This surface has no inline error slot for the carousel
        // upload (its catch only console.error'd), so route through
        // the store-level error toast -- the same channel
        // onBrandImageError uses for client-side validation failures.
        this.onBrandImageError(ARTWORK_UPLOAD_ERROR_EMPTY);
        return;
      }
      const form = new FormData();
      form.append("file", file);
      try {
        const resp = await this.$axios.post(
          `/stores/${this.storeId}/carousel_slides`,
          form,
        );
        // The custom upload endpoint
        // (api/src/endpoints/store_carousel_slides.py) returns a FLAT row
        // { id, store_id, display_order, headline, click_through_url,
        //   image_file_id, created_at } -- NOT the JSON:API
        // { data: { id, attributes } } envelope the SAFRS GET/PATCH
        // surface uses. Read the flat body directly so the pushed slide
        // carries image_file_id and its thumbnail renders immediately
        // (previously we read resp.data.data.attributes, which was
        // undefined for this endpoint -- the row had no image_file_id and
        // only a page reload's GET populated it).
        const row = resp?.data;
        if (row && row.id != null) {
          this.carouselSlides.push({ ...row, id: row.id });
          // Open the new slide's editor so its headline / URL / delete
          // controls appear right away. Without this, a store that had
          // zero slides showed the thumbnail but no editor until reload.
          // A pending edit on the previously selected slide still flushes
          // to THAT slide (see onSlideSave).
          this.selectedSlideId = String(row.id);
        }
        this.$emit("campaign-changed");
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Failed to upload carousel slide", describeError(err));
      }
    },

    async onSlideSave(payload) {
      // Resolve the slide by the id the editor tagged the payload with,
      // NOT by selectedSlide. Auto-save can FLUSH a pending edit for the
      // OUTGOING slide as the user switches thumbnails -- by the time that
      // save arrives, selectedSlideId has already moved to the new slide,
      // so keying off selectedSlide would PATCH the wrong row. Fall back to
      // selectedSlide ONLY for a legacy caller that omits the id: a tagged
      // save for a slide that is gone (the flush that follows deleting it)
      // must be dropped, not redirected onto whichever slide is now selected.
      const slide =
        payload.id != null
          ? this.carouselSlides.find(
              (s) => String(s.id) === String(payload.id),
            )
          : this.selectedSlide;
      if (!slide || !this.$axios) return;
      try {
        await this.$axios.patch(`/carousel_slides/${slide.id}/`, {
          data: {
            id: String(slide.id),
            type: "CarouselSlide",
            attributes: {
              headline: payload.headline,
              click_through_url: payload.click_through_url,
            },
          },
        });
        // Update local row so the editor + thumbnails reflect the save.
        slide.headline = payload.headline;
        slide.click_through_url = payload.click_through_url;
        this.$emit("campaign-changed");
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Failed to save carousel slide", describeError(err));
      }
    },

    async onSlideDelete() {
      const slide = this.selectedSlide;
      if (!slide || !this.$axios) return;
      try {
        await this.$axios.delete(`/carousel_slides/${slide.id}/`);
        this.carouselSlides = this.carouselSlides.filter(
          (s) => String(s.id) !== String(slide.id),
        );
        // Keep the editor open on a remaining slide (same rule as the
        // initial load); with none left, nothing is selected.
        const next = this.orderedSlides[0];
        this.selectedSlideId = next != null ? String(next.id) : null;
        this.$emit("campaign-changed");
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Failed to delete carousel slide", describeError(err));
      }
    },

    // --------------------------------------------------------------
    // E3: drag-reorder
    //
    // Native HTML5 drag-and-drop. We track the dragged slide's id in
    // component state (draggedSlideId) so onDrop can identify it
    // without depending on the DataTransfer object (which is awkward
    // to populate from mount tests).
    //
    // Reorder algorithm: remove the dragged slide from its current
    // position in orderedSlides, splice it at the target's position,
    // then renumber display_order across the whole list (0..N-1) and
    // PATCH each slide whose display_order changed.
    // --------------------------------------------------------------

    onDragStart(slide, event) {
      if (!this.editMode) {
        if (event && event.preventDefault) event.preventDefault();
        return;
      }
      this.draggedSlideId = String(slide.id);
      if (event && event.dataTransfer) {
        // Set effectAllowed so the browser shows the move cursor; the
        // payload itself is the in-memory draggedSlideId, not the DT.
        try {
          event.dataTransfer.effectAllowed = "move";
          event.dataTransfer.setData("text/plain", String(slide.id));
        } catch (_err) {
          // jsdom + some browsers throw on missing DataTransfer setup;
          // we don't depend on the value, so swallow.
        }
      }
    },

    onDragOver(slide, event) {
      // Required to allow drop. The .prevent modifier in the template
      // covers preventDefault; effectAllowed signals "move".
      if (event && event.dataTransfer) {
        try {
          event.dataTransfer.dropEffect = "move";
        } catch (_err) {
          // see onDragStart
        }
      }
    },

    onDragEnd() {
      this.draggedSlideId = null;
    },

    async onDrop(targetSlide) {
      const draggedId = this.draggedSlideId;
      this.draggedSlideId = null;
      if (!draggedId || !targetSlide) return;
      if (String(targetSlide.id) === String(draggedId)) return;

      // Work off the current display_order projection.
      const ordered = [...this.orderedSlides];
      const fromIdx = ordered.findIndex((s) => String(s.id) === draggedId);
      const toIdx = ordered.findIndex(
        (s) => String(s.id) === String(targetSlide.id),
      );
      if (fromIdx === -1 || toIdx === -1) return;

      const [moved] = ordered.splice(fromIdx, 1);
      ordered.splice(toIdx, 0, moved);

      // Optimistic local update: assign new display_order values to
      // the source rows so orderedSlides re-projects correctly.
      const changed = [];
      ordered.forEach((slide, idx) => {
        const row = this.carouselSlides.find(
          (s) => String(s.id) === String(slide.id),
        );
        if (row && row.display_order !== idx) {
          const before = row.display_order;
          row.display_order = idx;
          changed.push({ row, before });
        }
      });

      if (changed.length === 0 || !this.$axios) return;

      // PATCH each affected slide. Serial is fine for up to 5 slides;
      // failures revert local state and bubble a console warning. We
      // keep going on partial failure so the UI doesn't get stuck in
      // a half-reverted state -- the parent toast plumbing covers
      // user-facing error surfacing.
      const failures = [];
      for (const { row, before } of changed) {
        try {
          await this.$axios.patch(`/carousel_slides/${row.id}/`, {
            data: {
              id: String(row.id),
              type: "CarouselSlide",
              attributes: { display_order: row.display_order },
            },
          });
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error("Failed to reorder carousel slide", row.id, describeError(err));
          row.display_order = before;
          failures.push(row.id);
        }
      }
      if (failures.length === 0) {
        this.$emit("campaign-changed");
      }
    },
  },
};
</script>

<style scoped>
.branding {
  display: flex;
  flex-direction: column;
  gap: var(--tf-sp-4, 16px);
  padding: var(--tf-sp-4, 16px);
}

/* ---------- Section intro ---------- */
.branding-intro {
  margin-bottom: var(--tf-sp-1, 4px);
}

.branding-intro__eyebrow {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 1.4px;
  text-transform: uppercase;
  color: var(--tf-fg-4);
  margin-bottom: var(--tf-sp-1, 4px);
}

.branding-intro__lede {
  font-family: "Roboto", sans-serif;
  font-size: 14px;
  color: var(--tf-fg-2);
  margin: 0;
  line-height: 1.5;
  max-width: 60ch;
}

.branding-card__hint,
.branding-card__note {
  font-size: 13px;
  color: var(--tf-text-muted, var(--tf-fg-4));
  margin: 0;
}

.branding-card__note {
  font-style: italic;
}

.branding-contrast-note {
  margin-top: var(--tf-sp-2, 8px);
}

.branding-card__row {
  display: flex;
  align-items: center;
  gap: var(--tf-sp-2, 8px);
}

/* ---------- Brand identity card (ADR 0058) ---------- */
/* Brand image (left) + brand color (right), side-by-side; stacks to one
   column on narrow widths (ADR 0024 responsive boundary). */
.branding-identity {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--tf-sp-4, 16px);
  align-items: start;
}

.branding-identity__col {
  display: flex;
  flex-direction: column;
  gap: var(--tf-sp-2, 8px);
  min-width: 0;
}

.branding-field__label {
  font-size: 13px;
  font-weight: 500;
  color: var(--tf-fg-2);
}

@media (max-width: 720px) {
  .branding-identity {
    grid-template-columns: 1fr;
  }
}

.branding-color-swatch {
  width: 48px;
  height: 36px;
  padding: 0;
  border: 1px solid var(--tf-border, var(--tf-border));
  border-radius: var(--tf-radius-1, 4px);
  cursor: pointer;
  background: transparent;
}

.branding-color-hex {
  flex: 1;
  max-width: 160px;
  padding: 6px 10px;
  border: 1px solid var(--tf-border, var(--tf-border));
  border-radius: var(--tf-radius-1, 4px);
  font-family: monospace;
  font-size: 14px;
}

/* --- PMS (Pantone) brand-color picker ------------------------------ */

.branding-pms__selected {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 8px;
  font-size: 14px;
}

.branding-pms__selected-swatch,
.branding-pms__option-swatch {
  display: inline-block;
  width: 18px;
  height: 18px;
  border: 1px solid var(--tf-border, #d0d0d0);
  border-radius: var(--tf-radius-1, 4px);
  flex: 0 0 auto;
}

.branding-pms__clear {
  background: none;
  border: none;
  padding: 0;
  color: var(--tf-link, #1976d2);
  cursor: pointer;
  font-size: 13px;
  text-decoration: underline;
}

.branding-pms__clear:disabled,
.branding-pms__freetext-toggle:disabled {
  /* Disabled controls are exempt from the AA minimum and SHOULD read faint.
     --tf-text-disabled is not defined anywhere, so this silently painted the
     raw pre-rebase #9e9e9e; --tf-fg-5 is the actual disabled-state token. */
  color: var(--tf-fg-5);
  cursor: default;
}

.branding-pms__search,
.branding-pms__freetext-input,
.branding-pms__freetext-hex {
  width: 100%;
  max-width: 320px;
  padding: 6px 10px;
  border: 1px solid var(--tf-border, #d0d0d0);
  border-radius: var(--tf-radius-1, 4px);
  font-size: 14px;
}

.branding-pms__freetext-hex {
  font-family: monospace;
}

/* The result list is capped in JS (PMS_MAX_VISIBLE) but still scrolls,
   so a broad query cannot push the rest of the card off-screen. */
.branding-pms__list {
  list-style: none;
  margin: 6px 0;
  padding: 0;
  max-width: 320px;
  max-height: 220px;
  overflow-y: auto;
  border: 1px solid var(--tf-border, #d0d0d0);
  border-radius: var(--tf-radius-1, 4px);
}

.branding-pms__option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  cursor: pointer;
  font-size: 14px;
}

.branding-pms__option:hover {
  background: var(--tf-surface-hover, #f5f5f5);
}

.branding-pms__freetext {
  margin: 8px 0;
}

.branding-pms__freetext-hex-label {
  margin-top: 8px;
}

.branding-pms__error {
  margin: 6px 0 0;
  color: var(--tf-error, #c62828);
  font-size: 13px;
}

.branding-pms__approximation {
  margin-top: 8px;
}

.branding-welcome-editor {
  width: 100%;
}

/* Constrain the CKEditor editable so a long message scrolls rather than
   pushing the card taller indefinitely. */
.branding-welcome-editor :deep(.ck-editor__editable) {
  min-height: 120px;
  max-height: 320px;
}

/* ---------- Hero carousel (E2) ---------- */
.branding-carousel {
  display: flex;
  flex-direction: column;
  gap: var(--tf-sp-3, 12px);
}

.branding-carousel__strip {
  display: flex;
  flex-wrap: wrap;
  gap: var(--tf-sp-2, 8px);
  align-items: center;
}

/* Sizing guidance sits above the thumbnail strip -- read it before you
   upload. Constrained to a comfortable measure like the section lede. */
/* No max-width: the copy wraps to the card, not to an arbitrary measure.
   This carried max-width: 60ch when it shipped, which hard-wrapped it
   mid-card and read as a ragged short column beside full-width siblings
   (Jake, 2026-08-12). The card already bounds the line length. */
.branding-carousel__size-guidance {
  line-height: 1.5;
}

.branding-carousel__interval {
  display: flex;
  flex-direction: column;
  gap: var(--tf-sp-1, 4px);
  max-width: 360px;
}

/* Min | slider | max, so the 2s / 15s bounds flank the track. */
.branding-carousel__interval-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.branding-carousel__interval-slider {
  flex: 1 1 auto;
}

.branding-carousel__interval-bound {
  flex: 0 0 auto;
  font-size: 12px;
  color: var(--tf-fg-4, #64748b);
  font-variant-numeric: tabular-nums;
}

/* Persistent current-value readout under the knob (the drag-only thumb
   tooltip is not enough -- Jake wants the setting always visible). */
.branding-carousel__interval-value {
  margin: 0;
  text-align: center;
  font-size: 13px;
  font-weight: 600;
  color: var(--tf-fg-2, #334155);
  font-variant-numeric: tabular-nums;
}

.branding-carousel__slide {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--tf-sp-1, 4px);
}

.branding-carousel__thumb-dims {
  font-size: 11px;
  color: var(--tf-fg-4);
  line-height: 1.2;
  text-align: center;
  white-space: nowrap;
}

.branding-carousel__thumb {
  width: 120px;
  height: 80px;
  border: 1px solid var(--tf-border);
  border-radius: var(--tf-radius-1, 4px);
  overflow: hidden;
  cursor: pointer;
  background: var(--tf-bg-2, var(--tf-bg));
  display: flex;
  align-items: center;
  justify-content: center;
}

.branding-carousel__thumb--active {
  outline: 2px solid var(--tf-primary);
  outline-offset: 1px;
}

.branding-carousel__thumb--dragging {
  opacity: 0.4;
}

.branding-carousel__thumb[draggable="true"] {
  cursor: grab;
}

.branding-carousel__thumb[draggable="true"]:active {
  cursor: grabbing;
}

.branding-carousel__thumb-img {
  max-width: 100%;
  max-height: 100%;
  object-fit: cover;
}

.branding-carousel__add {
  width: 120px;
  height: 80px;
  border: 1px dashed var(--tf-border);
  border-radius: var(--tf-radius-1, 4px);
  background: transparent;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  color: var(--tf-fg-4);
  font-size: 13px;
}

.branding-carousel__add--disabled,
.branding-carousel__add[disabled] {
  opacity: 0.45;
  cursor: not-allowed;
}

.branding-carousel__add-label {
  font-size: 12px;
}

.branding-carousel__file-input {
  display: none;
}
</style>
